/**
 * useAccountOrderDetail — ViewModel for /account/orders/:id.
 *
 * Per SOP 02 §"MVVM Pattern" + SOP 15 Ch.20 (SOLID — S/D). Owns the
 * single-order fetch lifecycle so the page is purely presentational.
 *
 * 404 is modelled explicitly via `notFound` so the page can render the
 * empty-state CTA without parsing the error message string.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { APIROUTES, fillPath } from '../../constants/routes.constants'
import { mutateJson } from '../../utils/api/mutateJson'
import type { Order } from '../../models/order.model'

interface ApiEnvelope<T> {
  readonly success: boolean
  readonly data: T
  readonly error?: { readonly message: string }
}

export interface UseAccountOrderDetailReturn {
  readonly order: Order | null
  readonly isLoading: boolean
  readonly error: string | null
  readonly notFound: boolean
  readonly reload: () => void
  /** Customer Return button — POST /account/orders/:id/return with the
   *  reason text. Kiran 2026-06-17. */
  readonly fileReturn: (reason: string) => Promise<{ ok: true } | { ok: false; message: string }>
  /** Customer Cancel-return button — DELETE /account/orders/:id/return.
   *  Soft-deletes the return + reverts order.status to 'delivered'.
   *  Kiran 2026-06-18. */
  readonly cancelReturn: () => Promise<{ ok: true } | { ok: false; message: string }>
  /** Customer Cancel-order button — POST /account/orders/:id/cancel.
   *  Flips status to 'cancelled' (terminal). Available only while the
   *  order is `confirmed` or `packed` AND within CANCEL.WINDOWDAYS.
   *  Kiran 2026-06-19. */
  readonly cancelOrder: () => Promise<{ ok: true } | { ok: false; message: string }>
}

export function useAccountOrderDetail(id: string | undefined): UseAccountOrderDetailReturn {
  const { authedFetch } = useAuth()
  const [order, setOrder] = useState<Order | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState<boolean>(false)
  const [reloadKey, setReloadKey] = useState<number>(0)

  useEffect(() => {
    if (id === undefined) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    setIsLoading(true)
    setError(null)
    setNotFound(false)
    void (async () => {
      try {
        const res = await authedFetch(fillPath(APIROUTES.ORDERDETAIL, { id }))
        if (res.status === 404) {
          if (!cancelled) setNotFound(true)
          return
        }
        const body = (await res.json()) as ApiEnvelope<Order>
        if (!body.success) throw new Error(body.error?.message ?? `HTTP ${res.status}`)
        if (!cancelled) setOrder(body.data)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load order')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authedFetch, id, reloadKey])

  const reload = useCallback((): void => {
    setReloadKey((k) => k + 1)
  }, [])

  const fileReturn = useCallback(
    async (reason: string) => {
      if (id === undefined) return { ok: false as const, message: 'No order id' }
      const r = await mutateJson<{ return_id: string }>(
        authedFetch,
        fillPath(APIROUTES.ACCOUNTORDERRETURN, { id }),
        'POST',
        { reason }
      )
      if (r.ok) {
        // Order_returns row is created synchronously, but the status
        // transition goes through ORDER_STATUS_QUEUE. Wait ~1s for the
        // consumer to write the history row before the page reloads.
        await new Promise((resolve) => setTimeout(resolve, 1000))
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, id]
  )

  const cancelReturn = useCallback(
    async () => {
      if (id === undefined) return { ok: false as const, message: 'No order id' }
      const r = await mutateJson<{ status: string }>(
        authedFetch,
        fillPath(APIROUTES.ACCOUNTORDERRETURN, { id }),
        'DELETE'
      )
      if (r.ok) {
        // Status flip goes through ORDER_STATUS_QUEUE — wait ~1s for the
        // consumer to land the 'delivered' history row before reload.
        await new Promise((resolve) => setTimeout(resolve, 1000))
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, id]
  )

  const cancelOrder = useCallback(
    async () => {
      if (id === undefined) return { ok: false as const, message: 'No order id' }
      const r = await mutateJson<{ status: string }>(
        authedFetch,
        fillPath(APIROUTES.ACCOUNTORDERCANCEL, { id }),
        'POST'
      )
      if (r.ok) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, id]
  )

  return { order, isLoading, error, notFound, reload, fileReturn, cancelReturn, cancelOrder }
}
